/**
 * GA4 tracking helper — centralized, opinionated, side-effect-safe.
 *
 * Design:
 *  - GA4 script is loaded ONLY on production hostnames (revopdfloader.com and
 *    www.revopdfloader.com). On preview / localhost / other domains the
 *    helper runs in "dry-run" mode: it prints diagnostic logs to the console
 *    but never emits real events.
 *  - Only 3 events are supported for now:
 *      • sign_up                    (once per Stripe session)
 *      • begin_checkout             (before every redirect to Stripe)
 *      • revo_start_trial_confirmed (once per Stripe subscription in trialing)
 *  - Components never call gtag() directly — they use trackXxx() functions.
 *  - No PII (email/name/subscription_id/session_id) is EVER sent as an event
 *    parameter. Session/subscription IDs are used only as local dedup keys.
 */

const GA4_MEASUREMENT_ID = "G-0K4HY5QDDF";

// Centralized plan config so a price change is a one-liner.
export const PLAN_VALUE = 9.90;
export const PLAN_CURRENCY = "EUR";

// Production hostnames that should send real GA4 events. Everything else
// (preview, localhost, emergent subdomains) runs in dry-run mode.
const PRODUCTION_HOSTNAMES = new Set(["revopdfloader.com", "www.revopdfloader.com"]);

function isProduction() {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTNAMES.has(window.location.hostname.toLowerCase());
}

let initialized = false;

/**
 * Injects the GA4 script tag and configures gtag. Idempotent. Safe to call on
 * every mount. On non-production hostnames it is a no-op except for a log.
 */
export function initGA4() {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  if (!isProduction()) {
    console.info("[GA4] dry-run mode (hostname=%s)", window.location.hostname);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_MEASUREMENT_ID, {
    allow_ad_personalization_signals: false,
    // Use "auto" so GA4 picks the current registrable domain automatically
    // (works for both apex and www without any manual GA4 admin config).
    cookie_domain: "auto",
  });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  console.info("[GA4] loaded %s on %s", GA4_MEASUREMENT_ID, window.location.hostname);
}

/**
 * Fire an event and return a Promise that resolves either when GA4 confirms
 * the tag processing (event_callback) or after a hard safety timeout —
 * whichever happens first. Callers that need to navigate away right after
 * (e.g. redirect to Stripe) should `await` this to avoid losing the beacon.
 *
 * @param {number} timeoutMs  hard cap in ms; default 800
 * @returns {Promise<{delivered:boolean, dryRun:boolean, timeout:boolean}>}
 */
function emit(eventName, params = {}, timeoutMs = 800) {
  if (typeof window === "undefined") return Promise.resolve({ delivered: false, dryRun: true, timeout: false });
  if (!isProduction()) {
    console.info(`[GA4:dry-run] ${eventName}`, params);
    return Promise.resolve({ delivered: false, dryRun: true, timeout: false });
  }
  if (typeof window.gtag !== "function") {
    console.warn(`[GA4] gtag not available — event ${eventName} dropped`);
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false, reason: "no-gtag" });
  }
  // Production diagnostic log (no PII).
  console.info(`[GA4] emitting ${eventName}`);
  return new Promise((resolve) => {
    let settled = false;
    const settleWith = (result) => {
      if (settled) return;
      settled = true;
      console.info(`[GA4] ${eventName} result:`, result);
      resolve(result);
    };
    const safety = setTimeout(() => settleWith({ delivered: false, dryRun: false, timeout: true }), timeoutMs);
    window.gtag("event", eventName, {
      ...params,
      // Explicitly bind to OUR GA4 stream in case a landing/Framer container
      // is also injecting gtag on the same origin.
      send_to: GA4_MEASUREMENT_ID,
      // `beacon` uses navigator.sendBeacon which is the ONLY transport that
      // reliably survives an in-flight window.location.href navigation.
      transport_type: "beacon",
      // gtag will invoke the callback either after the beacon is queued or
      // after this timeout — keep in sync with the JS safety timeout above.
      event_timeout: timeoutMs,
      event_callback: () => {
        clearTimeout(safety);
        settleWith({ delivered: true, dryRun: false, timeout: false });
      },
    });
  });
}

// ---------- localStorage-backed dedup helpers ----------

function _readSentSet(storageKey) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_) {
    return new Set();
  }
}

function _writeSentSet(storageKey, set) {
  try {
    // Cap to last 20 entries so the key stays bounded.
    const arr = Array.from(set).slice(-20);
    window.localStorage.setItem(storageKey, JSON.stringify(arr));
  } catch (_) {
    // no-op if storage unavailable
  }
}

// ---------- sign_up ----------

const SIGNUP_STORAGE_KEY = "ga4_sign_up_sent_v1";

/**
 * sign_up — fired ONCE per Stripe checkout session (dedup by session_id
 * stored locally). Call after backend confirms a new user was created.
 *
 * Dedup is persisted ONLY after emit() confirms delivery (event_callback)
 * or when running in dry-run (preview). If GA4 times out, the dedup is
 * NOT stored, so a subsequent mount can retry — this avoids the bug where
 * a timed-out event permanently marks itself as "sent" and prevents any
 * future retry.
 *
 * @param {{signupKey: string}} args  signupKey is used ONLY for local dedup;
 *                                    it is never sent as an event parameter.
 * @returns {Promise<object>}
 */
export function trackSignUp({ signupKey } = {}) {
  if (!signupKey) {
    console.warn("[GA4] trackSignUp called without signupKey — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false });
  }
  const sent = _readSentSet(SIGNUP_STORAGE_KEY);
  if (sent.has(signupKey)) {
    console.info("[GA4] sign_up already sent for this checkout session — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false, deduped: true });
  }
  return emit("sign_up", { method: "email" }).then((result) => {
    // Persist dedup ONLY after successful delivery (or in dry-run where
    // there's nothing to deliver). On timeout we intentionally do NOT
    // store the key so the next mount can retry.
    if (result.delivered || result.dryRun) {
      const s2 = _readSentSet(SIGNUP_STORAGE_KEY);
      s2.add(signupKey);
      _writeSentSet(SIGNUP_STORAGE_KEY, s2);
    }
    return result;
  });
}

// ---------- begin_checkout ----------

/**
 * begin_checkout — fire and await BEFORE redirecting to Stripe.
 *
 * @returns {Promise<object>} resolves either on GA4 event_callback or after
 *   the 800 ms safety timeout, whichever comes first. Never rejects.
 */
export function trackBeginCheckout({ value = PLAN_VALUE, currency = PLAN_CURRENCY } = {}) {
  const params = {
    currency,
    items: [
      {
        item_id: "revo_pdf_loader_monthly",
        item_name: "REVO PDF Loader - Abbonamento mensile",
        price: PLAN_VALUE,
        quantity: 1,
      },
    ],
  };
  if (typeof value === "number") params.value = value;
  return emit("begin_checkout", params);
}

// ---------- revo_start_trial_confirmed ----------

const START_TRIAL_STORAGE_KEY = "ga4_revo_start_trial_confirmed_sent_v1";

/**
 * revo_start_trial_confirmed — fired ONCE per Stripe subscription after the
 * backend confirms status="trialing". The subscription id is used only as a
 * LOCAL dedup key in localStorage; it is NEVER sent as an event parameter.
 *
 * Dedup is persisted ONLY after emit() confirms delivery (event_callback)
 * or when running in dry-run (preview). If GA4 times out, the dedup is
 * NOT stored, so a subsequent mount can retry.
 *
 * The function name stays trackStartTrial() but the GA4 event name is now
 * `revo_start_trial_confirmed` to avoid mixing with the polluted legacy
 * `start_trial` history.
 *
 * @returns {Promise<object>}
 */
export function trackStartTrial({ subscriptionId, status } = {}) {
  if (!subscriptionId) {
    console.warn("[GA4] trackStartTrial called without subscriptionId — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false });
  }
  if (status !== "trialing") {
    console.info("[GA4] trackStartTrial skipped (status=%s, expected 'trialing')", status);
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false });
  }
  const sent = _readSentSet(START_TRIAL_STORAGE_KEY);
  if (sent.has(subscriptionId)) {
    console.info("[GA4] revo_start_trial_confirmed already sent for this subscription — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false, deduped: true });
  }
  // Never send the subscription id to GA4 — only used locally for dedup.
  return emit("revo_start_trial_confirmed", { currency: PLAN_CURRENCY }).then((result) => {
    // Persist dedup ONLY after successful delivery (or dry-run). On timeout
    // we intentionally do NOT store the key so the next mount can retry.
    if (result.delivered || result.dryRun) {
      const s2 = _readSentSet(START_TRIAL_STORAGE_KEY);
      s2.add(subscriptionId);
      _writeSentSet(START_TRIAL_STORAGE_KEY, s2);
    }
    return result;
  });
}


// ---------- revo_start_trial_confirmed (free trial variant) ----------

/**
 * Fire revo_start_trial_confirmed when the backend activates the free 7-day
 * trial at signup time. No Stripe subscription exists yet, so dedup is
 * keyed by the newly-created user id (used LOCALLY ONLY, never sent as a
 * GA4 event parameter).
 *
 * @param {{userId: string}} args
 * @returns {Promise<object>}
 */
export function trackStartTrialFree({ userId } = {}) {
  if (!userId) {
    console.warn("[GA4] trackStartTrialFree called without userId — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false });
  }
  const sent = _readSentSet(START_TRIAL_STORAGE_KEY);
  const key = `free:${userId}`;
  if (sent.has(key)) {
    console.info("[GA4] revo_start_trial_confirmed already sent for this free trial — skipped");
    return Promise.resolve({ delivered: false, dryRun: false, timeout: false, deduped: true });
  }
  return emit("revo_start_trial_confirmed", { currency: PLAN_CURRENCY }).then((result) => {
    if (result.delivered || result.dryRun) {
      const s2 = _readSentSet(START_TRIAL_STORAGE_KEY);
      s2.add(key);
      _writeSentSet(START_TRIAL_STORAGE_KEY, s2);
    }
    return result;
  });
}
